import React, { useCallback, useEffect, useState } from "react";
import { adminKpiApiService } from "../../../../infrastructure/api/admin/admin-kpi-api-service";
import type {
  CompanyKpi2Settings,
  UpdateCompanyKpi2SettingsRequest,
} from "../../../../domain/types/kpi";

// ─── KPI 보고 설정 섹션 ──────────────────────────────────────────────────────
// SMART_WORKSHOP 플러그인 단위 KPI 전역 설정 (plugin_sbiz24_detail.kpi_*). 백엔드:
//  GET   /api/admin/kpi/settings/plugin/{pluginId}
//  PATCH /api/admin/kpi/settings/plugin/{pluginId}
//
// 토글 규칙 (plan.md):
//  | KPI 보고 | 테스트 보고 | 결과                                          |
//  | ON       | OFF         | 정부 운영 URL 로 실 송신                       |
//  | ON       | ON          | 정부 테스트 URL 로 테스트 송신                 |
//  | OFF      | 잠김 (OFF)  | 외부 송신 없음 · 내부 DB 에만 적재 (MES 전용)  |
//
// pluginId(사업 상세) 단위 설정이다 — companyId 가 아니라 pluginId 로 조회/수정한다.
export const KpiReportSettingsSection: React.FC<{ pluginId: number | null }> = ({
  pluginId,
}) => {
  const [settings, setSettings] = useState<CompanyKpi2Settings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // form draft (저장 전 사용자 편집값)
  // 인증키는 회사 전역이 아니라 플러그인(공방 가입) 단위 — 카테고리 추가/수정에서 입력. 여기선 토글만.
  const [kpiEnabled, setKpiEnabled] = useState(false);
  const [kpiTestMode, setKpiTestMode] = useState(false);
  const [kpiAutoReport, setKpiAutoReport] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    if (pluginId === null || Number.isNaN(pluginId)) return;
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const response = await adminKpiApiService.getSbiz24Settings(pluginId);
      if (response.errorCode || !response.data) {
        throw new Error(response.errorMsg || "KPI 설정 조회 실패");
      }
      const data = response.data;
      setSettings(data);
      setKpiEnabled(data.kpiEnabled);
      // KPI 보고 OFF → 테스트 모드 강제 OFF
      setKpiTestMode(data.kpiEnabled ? data.kpiTestMode : false);
      setKpiAutoReport(data.kpiAutoReport);
    } catch (error) {
      console.error("KPI 회사 설정 조회 실패:", error);
      setLoadFailed(true);
      setSettings(null);
    } finally {
      setIsLoading(false);
    }
  }, [pluginId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // KPI 보고 OFF 이면 테스트 모드 자동 잠금 OFF
  const handleKpiEnabledChange = (next: boolean) => {
    setKpiEnabled(next);
    if (!next) setKpiTestMode(false);
  };

  const handleSave = async () => {
    if (pluginId === null || Number.isNaN(pluginId)) return;
    if (!settings) return;

    // 변경된 필드만 PATCH 로 전송 (null/미지정 필드는 기존 값 유지)
    const request: UpdateCompanyKpi2SettingsRequest = {};
    if (kpiEnabled !== settings.kpiEnabled) {
      request.kpiEnabled = kpiEnabled;
    }
    // 서버 측 정책: KPI 보고 OFF 이면 테스트 모드는 항상 OFF
    const normalizedTestMode = kpiEnabled ? kpiTestMode : false;
    if (normalizedTestMode !== settings.kpiTestMode) {
      request.kpiTestMode = normalizedTestMode;
    }
    if (kpiAutoReport !== settings.kpiAutoReport) {
      request.kpiAutoReport = kpiAutoReport;
    }

    if (Object.keys(request).length === 0) {
      alert("변경된 내용이 없습니다.");
      return;
    }

    setIsSaving(true);
    try {
      const response = await adminKpiApiService.updateSbiz24Settings(
        pluginId,
        request,
      );
      if (response.errorCode || !response.data) {
        throw new Error(response.errorMsg || "KPI 설정 저장 실패");
      }
      const data = response.data;
      setSettings(data);
      setKpiEnabled(data.kpiEnabled);
      setKpiTestMode(data.kpiEnabled ? data.kpiTestMode : false);
      setKpiAutoReport(data.kpiAutoReport);
      alert("저장되었습니다.");
    } catch (error) {
      console.error("KPI 회사 설정 저장 실패:", error);
      const message =
        error instanceof Error ? error.message : "KPI 설정 저장에 실패했습니다.";
      alert(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (pluginId === null || Number.isNaN(pluginId)) {
    return null;
  }

  // 변경 여부 — 로드된 settings 와 draft 비교 (저장 버튼 활성/비활성)
  const settingsDirty =
    !!settings &&
    (kpiEnabled !== settings.kpiEnabled ||
      kpiTestMode !== (settings.kpiEnabled ? settings.kpiTestMode : false) ||
      kpiAutoReport !== settings.kpiAutoReport);

  return (
    <section className="mb-5 border border-[#D9D9D9] rounded-[10px] bg-white overflow-hidden">
      <div className="px-5 py-3.5 border-b border-[#D9D9D9] flex items-center justify-between bg-[#FAFAFA]">
        <div>
          <h3 className="text-[14px] font-semibold">KPI 보고 설정</h3>
          <p className="text-[11.5px] text-[#888] mt-0.5">
            스마트제조 지원시스템 2.0 정부 보고 채널 · 자동 보고(목표값 fallback)
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || isLoading || !settings || !settingsDirty}
          className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-[#252525] text-white hover:bg-[#333] disabled:bg-[#BBB] disabled:cursor-not-allowed"
        >
          {isSaving ? "저장 중…" : "설정 저장"}
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* 로딩/실패 표시 */}
        {isLoading && !settings && (
          <div className="text-[12.5px] text-[#888]">설정을 불러오는 중입니다…</div>
        )}
        {loadFailed && !settings && (
          <div
            className="text-[12.5px] px-3 py-2 rounded border"
            style={{
              background: "#FEF2F2",
              borderColor: "rgba(242,74,74,0.2)",
              color: "#F24A4A",
            }}
          >
            KPI 설정을 불러오지 못했습니다.
          </div>
        )}

        {/* 인증키는 카테고리(공방 가입) 추가/수정에서 설정 — 여기선 보고 토글만 */}

        {/* 토글 3개 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SettingToggleCard
            label="KPI 보고"
            description="OFF 일 경우 외부 송신 없이 내부 DB 에만 적재됩니다 (MES 전용 모드)."
            value={kpiEnabled}
            onChange={handleKpiEnabledChange}
            disabled={!settings}
          />
          <SettingToggleCard
            label="테스트 보고"
            description={
              kpiEnabled
                ? "ON 일 경우 정부 테스트 URL 로 송신됩니다."
                : "KPI 보고가 OFF 이면 사용할 수 없습니다."
            }
            value={kpiTestMode}
            onChange={setKpiTestMode}
            disabled={!settings || !kpiEnabled}
          />
          <SettingToggleCard
            label="자동 보고 (목표값 fallback)"
            description="실측 입력이 없을 때 올해 목표값으로 자동 보고합니다. 실측 입력이 들어오면 항상 실측 우선."
            value={kpiAutoReport}
            onChange={setKpiAutoReport}
            disabled={!settings}
          />
        </div>
      </div>
    </section>
  );
};

// 토글 카드 (체크박스 형태)
const SettingToggleCard: React.FC<{
  label: string;
  description: string;
  value: boolean;
  onChange: (next: boolean) => void;
  disabled: boolean;
}> = ({ label, description, value, onChange, disabled }) => (
  <label
    className={`border rounded-md p-3 flex items-start gap-3 cursor-pointer transition-colors ${
      value
        ? "border-[#4285F4] bg-[#F5F8FF]"
        : "border-[#D9D9D9] bg-white hover:bg-[#FAFAFA]"
    } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
  >
    <input
      type="checkbox"
      checked={value}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      className="mt-0.5 w-4 h-4 accent-[#4285F4] flex-shrink-0"
    />
    <div className="flex-1 min-w-0">
      <div className="text-[13px] font-semibold text-[#252525]">{label}</div>
      <div className="text-[11.5px] text-[#888] mt-0.5 leading-relaxed">
        {description}
      </div>
    </div>
  </label>
);

export default KpiReportSettingsSection;